<script lang="ts">
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import RecentPendingRequests from './RecentPendingRequests.svelte';
  import RecentPendingPayments from './RecentPendingPayments.svelte';
  import RecentCompletedPayments from './RecentCompletedPayments.svelte';
  import {
    EmployerDashboardService,
    type EmployerDashboardData,
    type RecentPendingRequest,
    type RecentPendingPayment,
    type RecentCompletedPayment
  } from '../../../lib/api/employer';
  import { setEmployerPendingActions } from '../../../stores/employer-pending-actions.store';

  type MetricLink = { href: string; aria: string };
  type DashboardTab = 'pending-requests' | 'pending-payments' | 'completed-payments';

  let loading = true;
  let error: string | null = null;

  let pendingRequestsCount = 0;
  let pendingPaymentsCount = 0;
  let pendingPaymentsValue = 0;
  let completedPaymentsCount = 0;
  let completedPaymentsValue = 0;

  let recentPendingRequests: RecentPendingRequest[] = [];
  let recentPendingPayments: RecentPendingPayment[] = [];
  let recentCompletedPayments: RecentCompletedPayment[] = [];

  let activeTab: DashboardTab = 'pending-requests';

  $: showPendingRequestsBanner = !loading && pendingRequestsCount > 0;
  $: pendingRequestsBannerLabel =
    pendingRequestsCount === 1
      ? '1 donation request awaiting your review'
      : `${pendingRequestsCount} donation requests awaiting your review`;

  $: showPendingPaymentsBanner =
    !loading && (pendingPaymentsCount > 0 || pendingPaymentsValue > 0);
  $: pendingPaymentsBannerLabel =
    pendingPaymentsCount === 1
      ? '1 approved donation awaiting payment'
      : pendingPaymentsCount > 1
        ? `${pendingPaymentsCount} approved donations awaiting payment`
        : 'Approved donations awaiting payment';

  let dashboardMetrics = [
    {
      id: 'pending-requests',
      link: { href: '/leave-requests', aria: 'Review pending requests' } as MetricLink,
      label: 'Pending Requests',
      value: '0',
      icon: 'M4.84548 0C5.0291 0 5.2052 0.0729393 5.33504 0.202772C5.46488 0.332605 5.53782 0.508696 5.53782 0.692308V0.925846C5.85476 0.923385 6.19909 0.922462 6.5708 0.923077H11.4283C11.8 0.923077 12.1443 0.924 12.4613 0.925846V0.692308C12.4613 0.508696 12.5342 0.332605 12.664 0.202772C12.7939 0.0729393 12.97 0 13.1536 0C13.3372 0 13.5133 0.0729393 13.6432 0.202772C13.773 0.332605 13.8459 0.508696 13.8459 0.692308V0.970154C13.857 0.970154 13.8678 0.971077 13.8783 0.972923C14.5337 1.02277 15.0866 1.12892 15.5916 1.37169C16.4081 1.7602 17.081 2.39661 17.5144 3.19015C17.7794 3.68123 17.8929 4.21754 17.9474 4.84523C18 5.45631 18 6.21323 18 7.16215V11.76C18 12.7098 18 13.4677 17.9474 14.0778C17.892 14.7055 17.7794 15.2418 17.5144 15.732C17.0812 16.5259 16.4082 17.1626 15.5916 17.5514C15.0866 17.7942 14.5337 17.9003 13.8783 17.9502C13.2358 18 12.4382 18 11.4283 18H6.57172C5.56182 18 4.76424 18 4.12175 17.9502C3.46633 17.8994 2.91338 17.7942 2.40843 17.5514C1.59193 17.1629 0.918981 16.5265 0.485563 15.7329C0.220627 15.2418 0.107083 14.7055 0.0526182 14.0778C1.47873e-07 13.4668 0 12.7098 0 11.7609V7.16308C0 6.21323 1.47873e-07 5.45538 0.0526182 4.84523C0.108006 4.21754 0.220627 3.68123 0.485563 3.19108C0.918827 2.39719 1.5918 1.76044 2.40843 1.37169C2.91338 1.12892 3.46633 1.02277 4.12175 0.972923L4.15314 0.969231V0.692308C4.15314 0.508696 4.22608 0.332605 4.35592 0.202772C4.48576 0.0729393 4.66186 0 4.84548 0ZM4.15314 2.35938C3.62049 2.40554 3.27986 2.48862 3.00846 2.61969C2.45497 2.88132 1.99817 3.31096 1.70316 3.84738C1.59793 4.04308 1.52223 4.27754 1.47238 4.61538H16.5276C16.4768 4.27754 16.4011 4.04308 16.2959 3.84831C16.0013 3.31168 15.5448 2.88172 14.9915 2.61969C14.7192 2.48862 14.3786 2.40554 13.8459 2.35938V2.53846C13.8459 2.72207 13.773 2.89816 13.6432 3.028C13.5133 3.15783 13.3372 3.23077 13.1536 3.23077C12.97 3.23077 12.7939 3.15783 12.664 3.028C12.5342 2.89816 12.4613 2.72207 12.4613 2.53846V2.31046C12.1468 2.308 11.7929 2.30708 11.3997 2.30769H6.59941C6.20616 2.30769 5.8523 2.30862 5.53782 2.31046V2.53846C5.53782 2.72207 5.46488 2.89816 5.33504 3.028C5.2052 3.15783 5.0291 3.23077 4.84548 3.23077C4.66186 3.23077 4.48576 3.15783 4.35592 3.028C4.22608 2.89816 4.15314 2.72207 4.15314 2.53846V2.35938Z',
      bgColor: 'bg-amber-custom',
      iconColor: 'text-white',
      dotColor: '#FCB201',
      viewbox: '0 0 18 18'
    },
    {
      id: 'pending-payments',
      link: { href: '/employer/outstanding-invoices', aria: 'View pending payments' } as MetricLink,
      label: 'Pending Payments',
      value: '$0.00',
      icon: 'M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z',
      bgColor: 'bg-red-custom',
      iconColor: 'text-white',
      dotColor: '#FC6C6B',
      viewbox: '0 0 24 24'
    },
    {
      id: 'completed-payments',
      link: { href: '/employer/payment-history', aria: 'View completed payments' } as MetricLink,
      label: 'Completed Payments',
      value: '$0.00',
      icon: 'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z',
      bgColor: 'bg-teal-custom',
      iconColor: 'text-white',
      dotColor: '#2A9D8F',
      viewbox: '0 0 24 24'
    }
  ];

  function formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  function updateDashboardMetrics() {
    dashboardMetrics = dashboardMetrics.map((metric) => {
      if (metric.id === 'pending-requests') {
        return { ...metric, value: String(pendingRequestsCount) };
      }
      if (metric.id === 'pending-payments') {
        return { ...metric, value: formatCurrency(pendingPaymentsValue) };
      }
      if (metric.id === 'completed-payments') {
        return { ...metric, value: formatCurrency(completedPaymentsValue) };
      }
      return metric;
    });
  }

  async function loadDashboardData() {
    loading = true;
    error = null;

    try {
      const response = await EmployerDashboardService.getDashboard();

      if (response.success && response.data) {
        const data: EmployerDashboardData = response.data;
        pendingRequestsCount = data.pending_requests_count ?? 0;
        pendingPaymentsCount = data.pending_payments_count ?? 0;
        pendingPaymentsValue = data.pending_payments_value ?? 0;
        completedPaymentsCount = data.completed_payments_count ?? 0;
        completedPaymentsValue = data.completed_payments_value ?? 0;
        recentPendingRequests = data.recent_pending_requests ?? [];
        recentPendingPayments = data.recent_pending_payments ?? [];
        recentCompletedPayments = data.recent_completed_payments ?? [];
        setEmployerPendingActions(
          pendingRequestsCount,
          pendingPaymentsCount,
          pendingPaymentsValue
        );
        updateDashboardMetrics();
      } else {
        error = 'Failed to load dashboard data';
      }
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      error = 'An error occurred while loading dashboard data';
    } finally {
      loading = false;
    }
  }

  onMount(() => {
    loadDashboardData();
  });
</script>

<div class="employer-dashboard">
  <div class="dashboard-header">
    <p class="subtitle">Welcome to your employer dashboard</p>
  </div>

  {#if showPendingRequestsBanner}
    <div class="pending-requests-banner" role="status" aria-live="polite">
      <div class="banner-copy">
        <span class="banner-eyebrow">Action required</span>
        <p class="banner-title">{pendingRequestsBannerLabel}</p>
        <p class="banner-text">
          Employees have submitted leave donation requests that need your approval
          before they can proceed to payment.
        </p>
      </div>
      <button
        type="button"
        class="banner-action"
        on:click={() => push('/leave-requests')}
      >
        Review requests
      </button>
    </div>
  {/if}

  {#if showPendingPaymentsBanner}
    <div class="pending-payments-banner" role="status" aria-live="polite">
      <div class="banner-copy">
        <span class="banner-eyebrow">Action required</span>
        <p class="banner-title">{pendingPaymentsBannerLabel}</p>
        <p class="banner-text">
          {formatCurrency(pendingPaymentsValue)} is ready to pay to your selected charities.
          Download invoices and mark payments as complete when done.
        </p>
      </div>
      <button
        type="button"
        class="banner-action"
        on:click={() => push('/employer/outstanding-invoices')}
      >
        Review payments
      </button>
    </div>
  {/if}

  <div class="metrics-grid">
    {#if loading}
      {#each dashboardMetrics as metric}
        <div class="metric-card loading">
          <div class="loading-shimmer"></div>
        </div>
      {/each}
    {:else}
      {#each dashboardMetrics as metric}
        <a
          class="metric-card metric-card-link"
          href={metric.link.href}
          aria-label="{metric.label}: {metric.value}. {metric.link.aria}."
          on:click|preventDefault={() => push(metric.link.href)}
          on:keydown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              push(metric.link.href);
            }
          }}
        >
          <div class="metric-content">
            <div class="metric-left">
              <div class="metric-label-container">
                <span class="metric-dot" style="background-color: {metric.dotColor}"></span>
                <span class="metric-label">{metric.label}</span>
              </div>
              <div class="metric-value">{metric.value}</div>
            </div>
            <div class="metric-icon {metric.bgColor}" aria-hidden="true">
              <svg class="icon {metric.iconColor}" viewBox={metric.viewbox} fill="currentColor">
                <path d={metric.icon} />
              </svg>
            </div>
          </div>
        </a>
      {/each}
    {/if}
  </div>

  <div class="dashboard-content">
    <div class="tabs-container">
      <div class="tabs">
        <button
          class="tab-button"
          class:active={activeTab === 'pending-requests'}
          on:click={() => (activeTab = 'pending-requests')}
        >
          Pending Requests
        </button>
        <button
          class="tab-button"
          class:active={activeTab === 'pending-payments'}
          on:click={() => (activeTab = 'pending-payments')}
        >
          Pending Payments
        </button>
        <button
          class="tab-button"
          class:active={activeTab === 'completed-payments'}
          on:click={() => (activeTab = 'completed-payments')}
        >
          Completed Payments
        </button>
      </div>
    </div>

    {#if activeTab === 'pending-requests'}
      <RecentPendingRequests
        requests={recentPendingRequests}
        {loading}
        {error}
      />
    {:else if activeTab === 'pending-payments'}
      <RecentPendingPayments
        payments={recentPendingPayments}
        {loading}
        {error}
      />
    {:else}
      <RecentCompletedPayments
        payments={recentCompletedPayments}
        {loading}
        {error}
      />
    {/if}
  </div>
</div>

<style>
  .employer-dashboard {
    padding: 2rem 0;
    max-width: 1900px;
    margin: 0 auto;
  }

  .dashboard-header {
    margin-bottom: 2rem;
    padding: 0 2rem;
  }

  .subtitle {
    color: #6b7280;
    font-size: 1rem;
    margin: 0;
  }

  .pending-requests-banner {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1.5rem;
    margin: 0 2rem 1rem;
    padding: 1.25rem 1.5rem;
    border: 1px solid #fcd34d;
    border-radius: 12px;
    background: linear-gradient(135deg, #fffbeb 0%, #fff 72%);
    box-shadow: 0 4px 14px rgba(252, 178, 1, 0.12);
  }

  .pending-payments-banner {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1.5rem;
    margin: 0 2rem 2rem;
    padding: 1.25rem 1.5rem;
    border: 1px solid #fca5a5;
    border-radius: 12px;
    background: linear-gradient(135deg, #fef2f2 0%, #fff 72%);
    box-shadow: 0 4px 14px rgba(252, 108, 107, 0.12);
  }

  .banner-copy {
    flex: 1;
    min-width: 0;
  }

  .pending-requests-banner .banner-eyebrow {
    display: inline-block;
    margin-bottom: 0.35rem;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #b45309;
  }

  .pending-requests-banner .banner-title {
    margin: 0 0 0.35rem;
    font-size: 1.125rem;
    font-weight: 600;
    color: #92400e;
  }

  .pending-requests-banner .banner-text {
    margin: 0;
    font-size: 0.875rem;
    color: #a16207;
    line-height: 1.5;
    max-width: 40rem;
  }

  .pending-payments-banner .banner-eyebrow {
    display: inline-block;
    margin-bottom: 0.35rem;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #b91c1c;
  }

  .pending-payments-banner .banner-title {
    margin: 0 0 0.35rem;
    font-size: 1.125rem;
    font-weight: 600;
    color: #991b1b;
  }

  .pending-payments-banner .banner-text {
    margin: 0;
    font-size: 0.875rem;
    color: #b91c1c;
    line-height: 1.5;
    max-width: 40rem;
  }

  .banner-action {
    flex-shrink: 0;
    padding: 0.625rem 1.25rem;
    border: none;
    border-radius: var(--border-radius-button, 10px);
    background: var(--color-primary, #ffb400);
    color: #111827;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s ease, transform 0.1s ease;
    white-space: nowrap;
  }

  .banner-action:hover {
    background: var(--color-primary-hover, #f0a800);
  }

  .banner-action:active {
    transform: translateY(1px);
  }

  .banner-action:focus {
    outline: none;
  }

  .banner-action:focus-visible {
    outline: 2px solid var(--color-primary, #ffb400);
    outline-offset: 2px;
  }

  .metrics-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
    margin-bottom: 3rem;
    padding: 0 2rem;
  }

  .metric-card {
    background: white;
    border-radius: 12px;
    padding: 1rem 1.25rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
    border: 1px solid #f3f4f6;
    transition: transform 0.2s, box-shadow 0.2s;
    min-height: 90px;
    position: relative;
    overflow: hidden;
  }

  a.metric-card-link {
    text-decoration: none;
    color: inherit;
    cursor: pointer;
    display: block;
  }

  a.metric-card-link:focus {
    outline: none;
  }

  a.metric-card-link:focus-visible {
    outline: 2px solid var(--color-primary, #ffb400);
    outline-offset: 2px;
  }

  .metric-card.loading {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .loading-shimmer {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.5) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
  }

  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }

  .metric-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }

  .metric-content {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    height: 100%;
  }

  .metric-left {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .metric-label-container {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .metric-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .metric-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
    margin: 0;
  }

  .metric-value {
    font-size: 2rem;
    font-weight: 700;
    color: #111827;
    line-height: 1;
    margin-top: 0.75rem;
  }

  .metric-icon {
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .icon {
    width: 1.5rem;
    height: 1.5rem;
  }

  .dashboard-content {
    padding: 0 2rem;
  }

  .tabs-container {
    margin-bottom: 1.5rem;
  }

  .tabs {
    display: flex;
    gap: 1rem;
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 1px;
  }

  .tab-button {
    padding: 0.75rem 1.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .tab-button:hover {
    color: #111827;
  }

  .tab-button.active {
    color: #f0b429;
    border-bottom-color: #f0b429;
  }

  .bg-amber-custom {
    background-color: #FCB201;
  }

  .bg-red-custom {
    background-color: #FC6C6B;
  }

  .bg-teal-custom {
    background-color: #2A9D8F;
  }

  .text-white {
    color: white;
  }

  @media (max-width: 768px) {
    .employer-dashboard {
      padding: 0;
      max-width: 100%;
    }

    .dashboard-header,
    .dashboard-content,
    .metrics-grid,
    .pending-requests-banner,
    .pending-payments-banner {
      padding-left: 0;
      padding-right: 0;
    }

    .pending-requests-banner,
    .pending-payments-banner {
      margin-left: 0;
      margin-right: 0;
      flex-direction: column;
      align-items: stretch;
    }

    .banner-action {
      width: 100%;
    }

    .metrics-grid {
      grid-template-columns: 1fr;
      gap: 0.75rem;
      margin-bottom: 1.5rem;
    }

    .metric-value {
      font-size: 1.5rem;
      margin-top: 0.5rem;
    }

    .tabs {
      gap: 0;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }

    .tab-button {
      flex: 1;
      text-align: center;
      padding: 0.625rem 0.75rem;
      font-size: 0.8125rem;
      white-space: nowrap;
    }
  }
</style>
